import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { ShieldCheck, Check, X, LayoutGrid, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { cn } from "../lib/utils";

// Columns are scopes plus a virtual "everyone" (empty required_scopes list) column.
const SCOPE_COLUMNS = [
  { key: "__all__",        label: "Everyone",     hint: "No scope required" },
  { key: "administrator",  label: "Administrator", hint: "Admins & Super Admin" },
  { key: "engineering",    label: "Engineering" },
  { key: "service_delivery", label: "Service delivery" },
  { key: "compliance_admin", label: "Compliance admin" },
  { key: "security_admin", label: "Security admin" },
  { key: "billing_admin",  label: "Billing admin" },
];

function Cell({ checked, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={checked}
      data-testid={testid}
      className={cn(
        "w-7 h-7 rounded-md border flex items-center justify-center transition-colors",
        checked
          ? "bg-primary/15 border-primary/40 text-primary hover:bg-primary/25"
          : "bg-background border-border text-transparent hover:border-primary/40 hover:text-muted-foreground",
        disabled && "opacity-60 cursor-not-allowed"
      )}
    >
      {checked && <Check className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function AppsVisibilityMatrix() {
  const { toast } = useToast();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/apps");
      const list = Array.isArray(data) ? data : (data.apps || []);
      setApps(list);
    } catch {
      toast({ title: "Failed to load apps", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const byCat = {};
    for (const a of apps) {
      const cat = a.category || "other";
      (byCat[cat] ||= []).push(a);
    }
    return byCat;
  }, [apps]);

  const setScopes = async (app, nextScopes) => {
    setSavingId(app.id);
    // Optimistic update
    setApps((cur) => cur.map((a) => a.id === app.id ? { ...a, required_scopes: nextScopes } : a));
    try {
      await api.put(`/admin/apps/${app.id}`, { ...app, required_scopes: nextScopes });
    } catch {
      toast({ title: "Update failed", variant: "destructive" });
      load();
    } finally { setSavingId(null); }
  };

  const toggleScope = (app, scopeKey) => {
    const current = app.required_scopes || [];
    if (scopeKey === "__all__") {
      // Toggle Everyone → clear required_scopes (visible to all) OR restore previous state
      if (current.length === 0) return; // already everyone
      setScopes(app, []);
      return;
    }
    const has = current.includes(scopeKey);
    const next = has ? current.filter((s) => s !== scopeKey) : [...current, scopeKey];
    setScopes(app, next);
  };

  const isChecked = (app, scopeKey) => {
    const cur = app.required_scopes || [];
    if (scopeKey === "__all__") return cur.length === 0;
    return cur.includes(scopeKey);
  };

  if (loading) {
    return <div className="rounded-xl border border-border bg-card p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading matrix…</div>;
  }

  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="apps-visibility-matrix">
      <header className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" />
          <h3 className="text-[14px] font-semibold">Apps visibility matrix</h3>
          <span className="text-[11.5px] text-muted-foreground">Toggle who sees each app. Empty row = visible to everyone.</span>
        </div>
        <span className="text-[11px] text-muted-foreground">{apps.length} apps</span>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="bg-muted/30 border-b border-border">
              <th className="text-left font-semibold text-muted-foreground uppercase tracking-wide text-[10.5px] px-4 py-2 sticky left-0 bg-muted/30 z-10 min-w-[220px]">
                App
              </th>
              {SCOPE_COLUMNS.map((c) => (
                <th key={c.key} className="text-center font-semibold text-muted-foreground uppercase tracking-wide text-[10.5px] px-2 py-2 whitespace-nowrap">
                  <div>{c.label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([cat, list]) => (
              <React.Fragment key={cat}>
                <tr className="bg-background/60">
                  <td colSpan={SCOPE_COLUMNS.length + 1} className="px-4 py-1.5 text-[10.5px] uppercase tracking-wider font-semibold text-muted-foreground border-t border-border">
                    {String(cat).replace("_", " ")}
                  </td>
                </tr>
                {list.map((app) => (
                  <tr key={app.id} className={cn("border-t border-border hover:bg-muted/30", savingId === app.id && "opacity-70")}>
                    <td className="px-4 py-2 sticky left-0 bg-card z-[1] min-w-[220px]">
                      <div className="flex items-center gap-2">
                        <LayoutGrid className="w-3.5 h-3.5 text-muted-foreground" />
                        <div>
                          <div className="font-semibold">{app.name}</div>
                          {app.description && <div className="text-[11px] text-muted-foreground line-clamp-1 max-w-[300px]">{app.description}</div>}
                        </div>
                      </div>
                    </td>
                    {SCOPE_COLUMNS.map((c) => (
                      <td key={c.key} className="px-2 py-2 text-center">
                        <Cell
                          checked={isChecked(app, c.key)}
                          onClick={() => toggleScope(app, c.key)}
                          disabled={savingId === app.id}
                          testid={`matrix-cell-${app.id}-${c.key}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <footer className="px-4 py-2.5 border-t border-border bg-muted/20 text-[11.5px] text-muted-foreground flex items-center gap-4 flex-wrap">
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-primary" /> Access granted</span>
        <span className="inline-flex items-center gap-1"><X className="w-3 h-3" /> Hidden</span>
        <span>Super Admin and users with the Administrator scope always see every app.</span>
      </footer>
    </section>
  );
}
